import { inject, Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { take } from 'rxjs/operators';
import { Asset, Column, System2d, Upright } from '../asset/asset.dto';
import { AssetService } from '../asset/asset.service';
import { IssueCfgService } from '../config/issue-cfg.service';
import { Issue, LocalizedRiskLevel } from '../issue/issue.dto';
import { IssueService } from '../issue/issue.service';
import { SystemDrawingService } from '../system/system.service';
import { UprightService } from '../upright/upright.service';
import { SystemView, SystemViewRequest, UprightElevationView, UprightElevationViewRequest, WarehouseDataSource, WarehouseSourceKind, WarehouseSourceRef } from './warehouse-data-source';
import { buildAssetIssuesQuery, buildSystemIssuesQuery, buildSystemView } from './system-view.mapper';
import { buildUprightElevationView } from './upright-elevation-view.mapper';

/**
 * Live Firestore state. Every read here is the same call, with the same arguments, in the same
 * order as the pre-refactor SystemCanvasComponent.loadSystemData, so the current rendering path is
 * unchanged by construction.
 */
@Injectable()
export class CurrentWarehouseDataSource extends WarehouseDataSource {
  readonly kind: WarehouseSourceKind = 'current';

  private readonly systemDrawingService = inject(SystemDrawingService);
  private readonly uprightService = inject(UprightService);
  private readonly assetService = inject(AssetService);
  private readonly issueService = inject(IssueService);
  private readonly issueCfgService = inject(IssueCfgService);

  async listSystems(divisionId: string, section: string | null = null): Promise<System2d[]> {
    return this.systemDrawingService.list(divisionId, section);
  }

  async loadSystemView(req: SystemViewRequest): Promise<SystemView> {
    const ref: WarehouseSourceRef = { kind: this.kind, divisionId: req.divisionId };
    const system = await this.systemDrawingService.get(req.divisionId, req.systemId);

    if (!system) {
      return buildSystemView({ system: null, uprights: [], columns: [], assets: [], issues: [] }, req, ref, []);
    }

    const uprights: Upright[] = [...((await this.uprightService.list(system.divisionId, system._id)) ?? [])].sort((a: Upright, b: Upright): number => a.index - b.index);
    const assetIds: string[] = uprights.map((upright: Upright) => upright.assetId);

    const assets: Asset[] = await Promise.all(assetIds.map((id: string) => this.assetService.get(system.divisionId, id).catch((): Asset => null)));
    // listColumns splices the array it is given, so it never receives the caller's copy.
    const columns: Column[] = await this.uprightService.listColumns(system.divisionId, [...assetIds]);

    // No uprights means nothing to anchor an issue to; skip the query as the original did.
    const issues: Issue[] =
      req.includeIssues && uprights.length ? (await this.issueService.search(system.divisionId, buildSystemIssuesQuery(system.number, req.inspection))).result : [];

    const priorities: LocalizedRiskLevel[] = await this.fetchPriorities(system.divisionId);

    return buildSystemView({ system, uprights, columns, assets, issues }, req, ref, priorities);
  }

  async loadUprightElevationView(req: UprightElevationViewRequest): Promise<UprightElevationView> {
    const ref: WarehouseSourceRef = { kind: this.kind, divisionId: req.divisionId };
    const assets: Asset[] = (await Promise.all(req.assetIds.map((id) => this.assetService.get(req.divisionId, id).catch((): Asset => null)))).filter((asset): asset is Asset => !!asset);
    const previousAsset = req.previousAssetOf
      ? await this.assetService.getPreviousAsset(req.divisionId, req.previousAssetOf).catch((): Asset => null)
      : null;
    if (previousAsset) {
      assets.push(previousAsset);
    }

    const assetIds = [...new Set([...req.assetIds, ...(previousAsset ? [String(previousAsset._id)] : [])])];
    const uprights: Upright[] = (await Promise.all(assetIds.map((id) => this.uprightService.get(req.divisionId, id).catch((): Upright => null)))).filter(
      (upright): upright is Upright => !!upright,
    );
    // listColumns drains its input, so pass a fresh list just as the system-view path does.
    const columns: Column[] = await this.uprightService.listColumns(req.divisionId, [...assetIds], req.rowIndex);
    const issues: Issue[] =
      req.includeIssues && req.issueAssetId ? (await this.issueService.search(req.divisionId, buildAssetIssuesQuery(req.issueAssetId, req.inspection))).result : [];
    const priorities: LocalizedRiskLevel[] = await this.fetchPriorities(req.divisionId);

    return buildUprightElevationView({ assets, uprights, columns, issues }, req, ref, priorities);
  }

  private async fetchPriorities(divisionId: string): Promise<LocalizedRiskLevel[]> {
    return firstValueFrom(this.issueCfgService.getIssuePriorities(divisionId, undefined).pipe(take(1)));
  }
}
